import {Box, Stack, Typography} from "@mui/material";
import type {ResourceReference} from "../../../../../sdo/ResourceReference.ts";
import ContentFrameCard from "./ContentFrameCard.tsx";

interface ResourceReferenceDetailProps {
    reference: ResourceReference;
}

function ResourceReferenceDetail({reference}: ResourceReferenceDetailProps) {
    const displayName = reference.display && reference.display.trim().length > 0
        ? reference.display
        : reference.definitionKey;
    const frameCount = reference.frames ? reference.frames.length : 0;

    return (
        <Box sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            height: "100%",
            background: "linear-gradient(180deg, rgba(22, 30, 38, 0.4) 0%, rgba(14, 18, 24, 0.55) 100%)",
            borderRadius: "12px",
            border: "0.5px solid #3F464F",
            overflow: "hidden",
        }}>
            {/* Scrollable body */}
            <Box sx={{flex: 1, overflowY: "auto", padding: "12px 14px"}}>
                {/* Profile section: icon + display/type/definition */}
                <Stack direction="row" spacing={2} alignItems="flex-start" sx={{marginBottom: "16px"}}>
                    <img
                        src={`/images/resource_${reference.typeKey}.svg`}
                        width={48}
                        height={48}
                        alt=""
                        aria-hidden="true"
                        style={{opacity: 0.9}}
                        onError={(e) => { (e.target as HTMLImageElement).src = "/images/pages_white.svg"; }}
                    />
                    <Stack direction="column" spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                        <Typography sx={{
                            color: "#f2f5fa",
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            textAlign: "left",
                            wordBreak: "break-word",
                        }}>
                            {displayName}
                        </Typography>
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.55)",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                            textAlign: "left",
                        }}>
                            Type: <span style={{color: "#02cfee"}}>{reference.typeKey}</span>
                        </Typography>
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.55)",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: "uppercase",
                            textAlign: "left",
                        }}>
                            Definition: <span style={{color: "#02cfee"}}>{reference.definitionKey}</span>
                        </Typography>
                    </Stack>
                </Stack>

                {/* Content Frames section */}
                <Box sx={{
                    borderTop: "1px solid rgba(63, 70, 79, 0.5)",
                    paddingTop: "10px",
                    marginTop: "8px",
                }}>
                    <Typography sx={{
                        color: "#a8b8c0",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        textTransform: "uppercase",
                        textAlign: "left",
                        marginBottom: "8px",
                    }}>
                        Content Frames ({frameCount})
                    </Typography>
                    {frameCount === 0 ? (
                        <Typography sx={{
                            color: "rgba(242, 245, 250, 0.4)",
                            fontSize: "0.78rem",
                            fontStyle: "italic",
                            textAlign: "left",
                        }}>
                            No content frames associated with this reference.
                        </Typography>
                    ) : (
                        <Stack direction="column" spacing={1}>
                            {reference.frames.map((f) => (
                                <ContentFrameCard key={f.uid} frame={f}/>
                            ))}
                        </Stack>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default ResourceReferenceDetail;
